import React, { useState } from 'react';
import { X } from 'lucide-react';
import { capitalize } from 'lodash';
import { MANUAL_CHANGE_LINE_CLASS } from '../manualChangeIndicator';
import { CONFIDENCE_CATEGORIES } from './ContributionDetailModal';

/**
 * The "manually changed" chip presentation, shared by the recording edit form
 * and the mass-edit quick-edit modals so the two surfaces can't drift.
 *
 * A chip carrying a manual change gets the purple left line (drawn by
 * `.manual-change-line::before` — see manualChangeIndicator.ts / global.css)
 * plus a hover tooltip.
 */

export const MANUAL_CHANGE_TOOLTIP = 'Manually Changed';

/** Small floating tooltip that appears immediately on hover. Avoids the
 *  browser's native `title` delay. Positioned above the host element via an
 *  absolute child; pointer-events disabled so it never blocks clicks. */
export const ManualChangeTooltipBubble: React.FC<{ text: string }> = ({
  text,
}) => (
  <span
    role="tooltip"
    className="tw-absolute tw-bottom-full tw-left-1/2 -tw-translate-x-1/2 tw-mb-1 tw-px-2 tw-py-1 tw-bg-gray-900 tw-text-white tw-text-xs tw-font-medium tw-rounded tw-whitespace-pre-wrap tw-max-w-xs tw-z-50 tw-pointer-events-none tw-shadow-lg tw-border tw-border-gray-700"
  >
    {text}
  </span>
);

/** Wraps a chip / pill so the manual-change line lives on an outer container
 *  whose opacity stays at 1 even when the inner chip dims (disabled state).
 *  Also avoids the pixel offset that any transparent layout-reservation
 *  border on the chip would otherwise push into the absolute pseudo. Shows
 *  an instant-display "Manually Changed" tooltip on hover. */
export const ManualChangeChipWrap: React.FC<{
  edited: boolean;
  /** Extra className for the outer wrapper. Use this to preserve the chip's
   *  outer layout flow (e.g. `tw-inline-flex` so the wrapper sits like a
   *  chip in a flex/wrap row). */
  outerClassName?: string;
  children: React.ReactNode;
}> = ({ edited, outerClassName, children }) => {
  const [hover, setHover] = useState(false);
  if (!edited) return <>{children}</>;
  return (
    <div
      className={`${MANUAL_CHANGE_LINE_CLASS} ${
        outerClassName ?? 'tw-inline-flex'
      }`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {children}
      {hover && <ManualChangeTooltipBubble text={MANUAL_CHANGE_TOOLTIP} />}
    </div>
  );
};

/**
 * One artist pill.
 *
 * Deliberately shows the stage name and nothing else — featured status is not
 * reliably known for every artist, so it is never rendered. The list a chip
 * belongs to only ever holds featured / other-featured artists; demoting an
 * artist takes it out of that list entirely.
 */
export const ArtistChip: React.FC<{
  name: string;
  /** Draw the purple manual-change line (added via the manual-changes overlay,
   *  or added locally in this session). */
  edited: boolean;
  /** Omit to render a read-only chip (e.g. the last remaining artist). */
  onRemove?: () => void;
  removeTitle?: string;
}> = ({ name, edited, onRemove, removeTitle }) => (
  <ManualChangeChipWrap edited={edited}>
    <div className="tw-relative tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-bg-gray-700 tw-text-white">
      <span>{name}</span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="tw-ml-0.5 tw-opacity-60 hover:tw-opacity-100 tw-flex tw-items-center"
          title={removeTitle}
          aria-label={removeTitle ?? `Remove ${name}`}
        >
          <X size={12} />
        </button>
      )}
    </div>
  </ManualChangeChipWrap>
);

/**
 * One plain value pill (ISRC, and any other flat multi-value field).
 *
 * Same two indicators as the contribution chip: `edited` draws the purple
 * "Manually Changed" line for a change recorded on the server, `changed` draws
 * the pulsing dot for a value added in this editing session.
 */
export const ValueChip: React.FC<{
  value: string;
  /** Server-side manual change → purple line + "Manually Changed" tooltip. */
  edited: boolean;
  /** Unsaved local addition → pulsing dot. */
  changed?: boolean;
  /** Rendered after the value, e.g. the "· reassigned" tag on an ISRC. */
  suffix?: React.ReactNode;
  /** Dim the chip (read-only recording). */
  dimmed?: boolean;
  /** Omit to render a read-only chip. */
  onRemove?: () => void;
}> = ({ value, edited, changed = false, suffix, dimmed = false, onRemove }) => (
  <ManualChangeChipWrap edited={edited}>
    <div
      className={`tw-relative tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-bg-gray-700 tw-text-white ${
        dimmed ? 'tw-opacity-60' : ''
      }`}
    >
      {changed && (
        <span className="tw-absolute -tw-top-1 -tw-right-1 tw-w-2.5 tw-h-2.5 tw-rounded-full tw-bg-surfacePurple tw-border tw-border-gray-900 tw-animate-pulse-slow" />
      )}
      <span>{value}</span>
      {suffix}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="tw-ml-1 tw-text-gray-400 hover:tw-text-white tw-flex tw-items-center"
          title="Remove"
          aria-label={`Remove ${value}`}
        >
          <X size={12} />
        </button>
      )}
    </div>
  </ManualChangeChipWrap>
);

/** Pill colours come from the shared confidence-category palette; an unknown
 *  category falls back to the last ("Unknown") entry. */
export function getContributionPillStyle(categoryId: string): {
  backgroundColor: string;
  color: string;
} {
  const cat =
    CONFIDENCE_CATEGORIES.find((c) => c.id === categoryId) ??
    CONFIDENCE_CATEGORIES[4];
  return { backgroundColor: cat.color, color: cat.textColor };
}

/**
 * One contribution pill, coloured by data-quality (confidence) category.
 *
 * Two independent indicators can appear:
 *  - `edited` draws the purple "Manually Changed" line — the role carries a
 *    manual change recorded on the server (contract 4.5.2 `fields.contributions`).
 *  - `changed` draws the pulsing dot — the role was added or altered in this
 *    editing session and hasn't been saved yet.
 */
export const ContributionChip: React.FC<{
  role: string;
  confidenceCategory?: string;
  /** Server-side manual change → purple line + "Manually Changed" tooltip. */
  edited: boolean;
  /** Unsaved local add/edit → pulsing dot. */
  changed?: boolean;
  /** Tooltip for the pulsing dot state, e.g. "Newly added contribution". */
  changedTitle?: string;
  /** Click / Enter / Space on the label — opens the detail editor. */
  onSelect?: () => void;
  /** Omit to render a read-only chip (e.g. the last remaining contribution). */
  onRemove?: () => void;
}> = ({
  role,
  confidenceCategory,
  edited,
  changed = false,
  changedTitle,
  onSelect,
  onRemove,
}) => (
  <ManualChangeChipWrap edited={edited}>
    <div
      className={`tw-relative tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-uppercase tw-border-2 tw-border-transparent tw-transition-colors${
        onSelect ? ' tw-cursor-pointer hover:tw-border-white/50' : ''
      }`}
      style={getContributionPillStyle(confidenceCategory ?? '1')}
      title={
        edited
          ? undefined
          : changed
          ? changedTitle
          : onSelect
          ? 'Click to edit confidence category'
          : undefined
      }
    >
      {changed && (
        <span className="tw-absolute -tw-top-1 -tw-right-1 tw-w-2.5 tw-h-2.5 tw-rounded-full tw-bg-surfacePurple tw-border tw-border-gray-900 tw-animate-pulse-slow" />
      )}
      <span
        role={onSelect ? 'button' : undefined}
        tabIndex={onSelect ? 0 : undefined}
        onClick={
          onSelect
            ? (e) => {
                e.stopPropagation();
                onSelect();
              }
            : undefined
        }
        onKeyDown={
          onSelect
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  onSelect();
                }
              }
            : undefined
        }
      >
        {capitalize(role)}
      </span>
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="tw-ml-0.5 tw-opacity-60 hover:tw-opacity-100 tw-flex tw-items-center"
          title="Remove contribution"
          aria-label={`Remove ${role}`}
        >
          <X size={12} />
        </button>
      )}
    </div>
  </ManualChangeChipWrap>
);
